// @flow
import * as React from 'react';
import invariant from 'invariant';
import { getAddressComponents } from 'utils/googleAddresses';
import { getIn } from 'utils/functional';
import trackError from 'utils/trackError';
import RichError from 'utils/RichError';
import googleMaps from 'utils/googleMaps';
import deepEqual from 'react-fast-compare';
import Input from 'components/Input';

const getLatLongComponents = (place?: Place) => {
  // eslint-disable-next-line camelcase
  const { formatted_address, geometry = {} } = place || {};

  const result = {
    latLongAddress: formatted_address,
    latitude: undefined,
    longitude: undefined
  };

  const { location } = geometry;
  if (location) {
    result.latitude = location.lat();
    result.longitude = location.lng();
  }

  return result;
};

const getCity = (addressComponents: AddressComponents) =>
  addressComponents.locality ||
  addressComponents.postal_town ||
  addressComponents.sublocality_level_1 ||
  addressComponents.administrative_area_level_3 ||
  addressComponents.administrative_area_level_2;

// Converts 'raw' address components from places API into structured
// {line1, city, state, ...} object
const parseAddressComponents = (
  geocoderAddressComponent: Array<GeocoderAddressComponent>
) => {
  const addressComponents = getAddressComponents(geocoderAddressComponent);
  const streetNumber = addressComponents.street_number;
  const route = addressComponents.route;
  const city = getCity(addressComponents);
  const administrativeArea = addressComponents.administrative_area_level_1 || {
    long: getText('(no state)'),
    short: 'XA'
  };
  const state = administrativeArea.long;
  const stateCode = administrativeArea.short;
  const zipcode = addressComponents.postal_code;
  const country = getIn(addressComponents, 'country.short');

  const line1 = [];
  if (streetNumber) {
    line1.push(streetNumber);
  }
  if (route) {
    line1.push(route);
  }
  return { line1: line1.join(' '), city, state, stateCode, zipcode, country };
};

const serializeAddress = (place: Place) => {
  if (place.address_components) {
    const addressComponents = parseAddressComponents(place.address_components);
    const {
      line1,
      city,
      state,
      stateCode,
      zipcode,
      country
    } = addressComponents;
    return {
      line1,
      city,
      state,
      stateCode,
      zipcode,
      country,
      ...getLatLongComponents(place)
    };
  }
  return { line1: place.name };
};

type Props = {
  addressTypes: Array<string>,
  componentRestrictions: Object,
  onAutoCompleteSuggestionSelected: Object => void
};

export default class AutoCompleteAddressInput extends React.PureComponent<Props> {
  inputRef = React.createRef<HTMLInputElement>();

  autocomplete: ?Object;

  preventSubmitListener: ?Object;

  componentDidMount() {
    this.setupAddressAutocomplete();
  }

  componentWillReceiveProps(nextProps: any) {
    if (
      !deepEqual(
        nextProps.componentRestrictions,
        this.props.componentRestrictions
      ) &&
      this.autocomplete
    ) {
      this.autocomplete.setComponentRestrictions(
        nextProps.componentRestrictions
      );
    }
  }

  componentWillUnmount() {
    if (this.autocomplete) this.autocomplete.unbindAll();
    if (this.preventSubmitListener)
      google.maps.event.removeListener(this.preventSubmitListener);
  }

  setupAddressAutocomplete = () => {
    const input = this.inputRef.current;
    invariant(input, 'Maps places must attach to valid input element');

    googleMaps().then(maps => {
      // maps.places.Autocomplete changes input.autocomplete
      // to the value "off" through the asynchronously loaded `places_impl.js`.
      // http://maps.googleapis.com/maps-api-v3/api/js/33/4/places_impl.js
      // Chrome ignores autocomplete="off", but respects arbitrary autocomplete="foo" values
      // Since the change is asynchronous and from another script,
      // we listen for changes to the autocomplete attribute and
      // reset back to a "non-off" string
      this.autocomplete = new maps.places.Autocomplete(input, {
        types: this.props.addressTypes,
        componentRestrictions: this.props.componentRestrictions
      });
      this.autocomplete.addListener('place_changed', () => {
        if (this.autocomplete) {
          const place = this.autocomplete.getPlace();
          const serializedAddress = serializeAddress(place);
          this.props.onAutoCompleteSuggestionSelected(serializedAddress);
          // https://developers.google.com/maps/documentation/javascript/reference/places-widget#Autocomplete.getPlace  -- failure case
          if (!serializedAddress.city) {
            trackError(
              new RichError('Failed to fetch API result', JSON.stringify(place))
            );
          }
        }
      });
      this.preventSubmitListener = google.maps.event.addDomListener(
        input,
        'keydown',
        e => {
          if (e.keyCode === 13) {
            e.preventDefault();
          }
        }
      );
    });
  };

  render() {
    const {
      addressTypes,
      componentRestrictions,
      onAutoCompleteSuggestionSelected,
      ...inputProps
    } = this.props;
    return <Input ref={this.inputRef} autoComplete="off" {...inputProps} />;
  }
}
